import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import getContracts, { ResponseDto } from '@r1-frontend/api-domru/api-profile/v1/unauth/contract-asterisked';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import Toggle from '@r1-frontend/ui-react/components/controls/Toggle';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import Throbber from '@r1-frontend/ui-react/components/loaders/Throbber';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import { setAgreement } from '~/src/features/payments-abn/store/actionCreators';
import { defaultAgreementData, IAgreementData, TField, TMenuItem } from '~/src/features/payments-unauth-abn/types';
import { usePrepareQueryData } from '~/src/helpers/hooks/usePrepareQueryData';

import * as ST from './styled';

export interface ISelectAgreementProps {
    setIsCloseFrame: Dispatch<SetStateAction<boolean>>,
    isCloseFrame: boolean,
}

export type TOptionValue = 'phone' | 'agreement';

const CONTACT_ROW_TYPE = 3;

const PaymentType = ({
    setIsCloseFrame,
    isCloseFrame,
}: ISelectAgreementProps) => {
    const dispatch = useDispatch();
    const isDesktop = useMatchMedia(`(max-width: ${wideBreakpoints.sDesktop}) and (min-width: ${wideBreakpoints.tablet})`);
    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);

    const { agreement: queryAgreement, c: queryContact, s: querySum } = usePrepareQueryData();
    const [agreementData, setAgreementData] = useState<IAgreementData>(defaultAgreementData);
    const [agreementList, setAgreementList] = useState<IAgreementData[]>([]);
    const [activeFilter, setActiveFilter] = useState<TOptionValue>('phone');
    const [contactValue, setContactValue] = useState<TField>();
    const [notFound, setNotFound] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isPhone = activeFilter === 'phone';

    const agreementText = useMemo(() => (isDesktop || isMobile ? 'По договору' : 'По номеру договора'), [isDesktop, isMobile]);
    const phoneText = useMemo(() => (isDesktop || isMobile ? 'По телефону' : 'По номеру телефона'), [isDesktop, isMobile]);

    const notFoundText = notFound
        ? <ST.NotFoundText>
            {activeFilter === 'phone'
                ? 'Похоже, вы неверно ввели номер. Пожалуйста, попробуйте ещё раз.'
                : 'Такого договора не существует. Пожалуйста, попробуйте ещё раз.'
            }
        </ST.NotFoundText>
        : null;

    const menuItems = [
        {
            text: phoneText,
            value: 'phone',
            action: () => {
                dataLayerPush({
                    event: EVENTS.UAevent,
                    category: CATEGORIES.newPayment,
                    action: 'click payment by phone_number',
                });
            },
        },
        {
            text: agreementText,
            value: 'agreement',
            action: () => {
                dataLayerPush({
                    event: EVENTS.UAevent,
                    category: CATEGORIES.newPayment,
                    action: 'click payment by agreement',
                });
            },
        },
    ];

    const toggleLabels = useMemo(() => (
        {
            leftText: phoneText,
            rightText: agreementText,
        }
    ), [isDesktop, isMobile]);

    const [activeMenuItem, setActiveMenuItem] = useState<TMenuItem>(menuItems.filter(item => item.value === activeFilter)[0]);

    useEffect(() => {
        const currentItem = menuItems.filter((item: TMenuItem) => item.value === activeFilter)[0];
        setActiveMenuItem(currentItem);
    }, [activeFilter, isDesktop, isMobile]);

    useEffect(() => {
        if (isCloseFrame) {
            setContactValue(undefined);
            setAgreementList([]);
            setActiveFilter('phone');
            setAgreementData(defaultAgreementData);
        }
    }, []);

    useEffect(() => {
        if (queryContact || querySum) {
            setActiveFilter('agreement');
        }
    }, []);

    useEffect(() => {
        if (queryContact || querySum) {
            if (activeFilter === 'agreement') {
                setNotFound(false);
                setContactValue({ ...contactValue, value: (queryContact || queryAgreement) } as TField);
                getAgreementList(queryContact || queryAgreement);
            } else {
                setContactValue(undefined);
                setAgreementList([]);
            }
        }
    }, [queryContact, queryAgreement, setContactValue, activeFilter, setAgreementList]);

    const onChangeHandler = (value: TField) => {
        setIsCloseFrame(false);
        setContactValue(value);
        dispatch(setAgreement(''));
    };

    const getAgreementList = useCallback(async(value?: string) => {
        setIsCloseFrame(false);

        if (value) {
            setIsLoading(true);

            const resp = await doRequest.apiProfile<ResponseDto>(
                getContracts(value, false),
            );

            if (resp.isSuccess) {
                const contactList = resp.payload.contacts
                    .map((contact) => {
                        return {
                            label: contact.contactType === CONTACT_ROW_TYPE ? contact.row : contact.address,
                            value: contact.contactType === CONTACT_ROW_TYPE ? contact.row : contact.address,
                            contact: isPhone ? contact.row : value,
                            rowEnc: contact.rowEnc,
                            phone: isPhone ? value : '',
                        };
                    })
                    .filter((contact) => contact.label);

                const [firstContact] = contactList;

                setAgreementList(contactList ?? []);
                setAgreementData(firstContact ?? defaultAgreementData);
                setNotFound(!contactList?.length);
                dispatch(setAgreement(firstContact.contact, firstContact.rowEnc ?? undefined));
            } else {
                setAgreementList([]);
                dispatch(setAgreement(''));
                setNotFound(true);
            }
            setIsLoading(false);
        }
    }, [isPhone, activeFilter, setNotFound, queryContact, querySum]);

    const onMenuItemClick = useCallback((value) => {
        const valueItem = menuItems.filter(item => item.text === value)[0];
        if (valueItem) {
            setActiveMenuItem(valueItem);
            setAgreementList([]);
            setAgreementData(defaultAgreementData);
            setNotFound(false);
            setContactValue(undefined);
            valueItem.action();
            setActiveFilter(valueItem.value as TOptionValue);
        }
    }, [activeMenuItem, activeFilter]);

    return (
        <ST.InfoSection>
            <ST.InfoContainer>
                <Toggle
                    textsValues={toggleLabels}
                    onChangeCallback={onMenuItemClick}
                    activeTab={activeMenuItem.text}
                />
                {activeFilter === 'agreement'
                    ? <ST.StyledInput
                        key='agreement'
                        value={contactValue?.value}
                        type='agreement'
                        placeholder='Номер договора'
                        onChange={(value: TField) => onChangeHandler(value)}
                        onBlur={() => getAgreementList(contactValue?.value)}
                        isNotFound={notFound}
                        maxLength={'12'}
                        data-test='field-agreement'
                        className={'ym-disable-keys'}
                    />
                    : <ST.SPhoneInput
                        key='phone'
                        placeholder='Номер телефона'
                        initialValue={contactValue?.value}
                        onChange={(value: TField) => onChangeHandler(value)}
                        onBlur={() => getAgreementList(contactValue?.value)}
                        isNotValid={notFound}
                        data-test='field-phone'
                        className={'ym-disable-keys'}
                    />}
                <Throbber position={'absolute'} waitStatus={!isLoading} />
                {agreementList.length > 0
                    ? <ST.InfoLineTitle>{agreementList.length === 1
                        ? agreementData?.label
                        : <>
                            <ST.Text>К этому номеру привязано несколько адресов. Выберите, для какого адреса вы хотите пополнить лицевой счёт.</ST.Text>
                            <ST.SSelect
                                options={agreementList}
                                value={agreementData}
                                onChange={setAgreementData as () => void}
                            />
                        </>
                    }
                    </ST.InfoLineTitle>
                    : notFoundText
                }
            </ST.InfoContainer>
        </ST.InfoSection>
    );
};

export default PaymentType;
